import type { Size, Vector2 } from '@sorrem/shared';
import type { UISkillTreeView } from '@sorrem-client/types';
import type { ValueStore } from '@sorrem-client/ui/shared/value-store';
import { SKILL_TREE_BOUNDS, type SkillTreeBounds } from './skill-tree-layout';
import { clamp } from './skill-tree-math';

/** Zoom range in CSS px per tree unit: a node (120 units) spans about 30 px to 180 px. */
export const SKILL_TREE_MIN_ZOOM = 0.25;
export const SKILL_TREE_MAX_ZOOM = 1.5;
/** Steps for the zoom buttons. */
export const SKILL_TREE_ZOOM_LEVELS: readonly number[] = [0.25, 0.5, 0.75, 1, 1.25, 1.5];
/** Zoom the tree opens at. */
export const SKILL_TREE_DEFAULT_ZOOM = 0.5;

export const INITIAL_SKILL_TREE_VIEW: UISkillTreeView = { center: { x: 0, y: 0 }, zoom: SKILL_TREE_DEFAULT_ZOOM };

/**
 * The pan/zoom the tree and the sky share. It changes on every drag and
 * wheel event, so it lives in a store the layers write to the DOM from,
 * rather than in React state that would re-render the page each time.
 */
export type SkillTreeViewStore = ValueStore<UISkillTreeView>;

/** Keeps the zoom in range and the center inside the tree's bounds, so the tree can never be panned off screen. */
export function clampSkillTreeView(view: UISkillTreeView, bounds: SkillTreeBounds = SKILL_TREE_BOUNDS): UISkillTreeView {
    return {
        zoom: clamp(view.zoom, SKILL_TREE_MIN_ZOOM, SKILL_TREE_MAX_ZOOM),
        center: {
            x: clamp(view.center.x, bounds.min.x, bounds.max.x),
            y: clamp(view.center.y, bounds.min.y, bounds.max.y),
        },
    };
}

/** A rectangle in CSS pixels, relative to the tree's viewport. */
export interface SkillTreeFrameRegion {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
}

/**
 * Centers the tree in `region` (the part of a `viewport`-sized view not
 * covered by other UI) at `zoom`. Without a region it centers in the whole
 * viewport.
 */
export function centerSkillTreeView(
    viewport: Size,
    region: SkillTreeFrameRegion = { left: 0, top: 0, width: viewport.width, height: viewport.height },
    bounds: SkillTreeBounds = SKILL_TREE_BOUNDS,
    zoom: number = SKILL_TREE_DEFAULT_ZOOM,
): UISkillTreeView {
    // Offset of the region's center from the viewport's center, in CSS pixels.
    const offset = {
        x: region.left + region.width / 2 - viewport.width / 2,
        y: region.top + region.height / 2 - viewport.height / 2,
    };
    return clampSkillTreeView({
        zoom,
        center: {
            x: (bounds.min.x + bounds.max.x) / 2 - offset.x / zoom,
            y: (bounds.min.y + bounds.max.y) / 2 - offset.y / zoom,
        },
    }, bounds);
}

/** Pans by a screen-space drag of `delta` CSS pixels (the tree follows the pointer). */
export function panSkillTreeView(view: UISkillTreeView, delta: Vector2): UISkillTreeView {
    return clampSkillTreeView({
        zoom: view.zoom,
        center: { x: view.center.x - delta.x / view.zoom, y: view.center.y - delta.y / view.zoom },
    });
}

/**
 * Zooms to `zoom`, keeping the tree point under `focus` fixed on screen.
 * `focus` is in CSS pixels from the viewport's center (zero zooms about the center).
 */
export function zoomSkillTreeViewAt(view: UISkillTreeView, zoom: number, focus: Vector2): UISkillTreeView {
    const nextZoom = clamp(zoom, SKILL_TREE_MIN_ZOOM, SKILL_TREE_MAX_ZOOM);
    const anchor = { x: view.center.x + focus.x / view.zoom, y: view.center.y + focus.y / view.zoom };
    return clampSkillTreeView({
        zoom: nextZoom,
        center: { x: anchor.x - focus.x / nextZoom, y: anchor.y - focus.y / nextZoom },
    });
}
